import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { Moment } from 'moment';
import { BehaviorSubject, Observable, Subscription, timer, merge } from 'rxjs';
import { take, takeWhile, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { TimeRemaining } from './expiration-by-country.enum';
import { TranslateService } from '@ngx-translate/core';
import { OrderDetailQueryParams } from '@ordersnproducts/modules/orders-and-product-catalog/modules/order-detail/constants/order-detail-query-params';
import { Store } from '@ngrx/store';
import { getParameters, State } from '@cemex/cmx-client-layout';

export const ORDERS_TIMEOUT_PARAMETER_CODE = 'OLC_ORDERS_TIMESLOT_TIMEOUT_MINUTES';
export const MODIFY_TIMEOUT_PARAMETER_CODE = 'OLC_ORDERS_MODIFY_PROCESS_TIMEOUT_MINUTES';

@Injectable({
  providedIn: 'root',
})
export class ExpirationTimeService implements OnDestroy {
  /**
   * @description Confirms whether the time is expired or not
   */
  public isTimeExpired$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  /**
   * @description Confirms whether the time is expired or not for modify
   */
  public isTimeModifyExpired$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  /**
   * @description Emits missing minutes
   */
  public missingMinutesExpiration$: BehaviorSubject<TimeRemaining> = new BehaviorSubject(null);

  /**
   * @description Emits missing minutes
   */
  public missingMinutesModifyExpiration$: BehaviorSubject<TimeRemaining> = new BehaviorSubject(null);

  /**
   * @description Emits when modal closes
   */
  public expirationModalClose$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  /**
   * @description Emits when modal closes
   */
  public expirationModifyModalClose$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  /**
   * @description Sets expiration time minutes
   */
  public expirationTimeMinutes = 10;

  /**
   * @description Sets expiration time minutes modify
   */
  public expirationTimeMinutesModify = 10;

  /**
   * @description Path of order form
   */
  public orderFormPath: string;

  /**
   * Confirms if the event is comming fron summaries
   */
  public isFromSummary = false;

  /**
   * Emits when timeExpiration and timeExpirationModify finishes
   */
  public globalExpiration$: Observable<boolean>;

  /**
   * @description Path change subscription
   */
  private ruterSubscription$: Subscription;

  /**
   * @description Activates and deactivates timer
   */
  public isCountdownActive = false;

  /**
   * @description Activates and deactivates timer modify
   */
  public isCountdownModifyActive = false;

  /**
   * @description List of last pages visited
   */
  public pathsVisited: string[] = [];

  /**
   * @description Delays expiration time modal when loading state
   */
  public delayET = false;

  /**
   * @description Order data to be modified
   */
  public orderParamsModify: OrderDetailQueryParams;

  /**
   * @description
   */
  public isMultiProdcuct = false;

  /**
   * Form id validated
   */
  public formValidated: number;

  /**
   *  @description List of paths where the expiration time will works
   */
  private expirationTimepaths: string[] = [];

  /**
   * @description Sets the time when the expiration time will occurs
   */
  private lastExpirationTime: Moment;

  /**
   * @description Sets the time when the expiration time will occurs
   */
  private lastExpirationTimeModify: Moment;

  /**
   * @description Missing minutes until expiration time
   */
  private missingMinutes: TimeRemaining = {};

  /**
   * @description Missing minutes until expiration time modify
   */
  private missingMinutesModify: TimeRemaining = {};

  /**
   * @description Current path
   */
  private currentPath: string;

  /**
   * @description Unsuscribe modal suscription
   */
  private isModalSubscriptionActive = false;

  /**
   * @description Unsuscribe modal modify suscription
   */
  private isModalSubscriptionModifyActive = false;

  constructor(
    private router: Router,
    public translate: TranslateService,
    private store: Store<State>,
  ) {
    this.globalExpiration$ = merge(this.isTimeExpired$, this.isTimeModifyExpired$);
    this.pathsVisited.push(this.router.url);
    this.ruterSubscription$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Current path
        this.currentPath = event['url'];
        if (!this.pathsVisited.includes(event['url'])) {
          this.pathsVisited.push(event['url']);
        }

        // Filter paths in order to detects whether the navigation occurs where expiration time works
        const isInExpirationTimePath = this.expirationTimepaths.filter((path) => this.currentPath.includes(path));

        // Verifies whether the user is navigating where the expiration time works
        if (!!!isInExpirationTimePath.length) {
          this.stopCountdown();
          this.stopCountdownModify();
          this.isTimeModifyExpired$.next(null);
          this.isTimeExpired$.next(null);
        }
      }
    });
  }

  public setExpiratioTimePaths(expirationTimePaths: string[], isModify: boolean = false, country: string = null): void {
    this.expirationTimepaths = expirationTimePaths;
    if (isModify) {
      this.setExpirationTimeModify();
      this.startCountdownModify();
    }
  }

  public startCountdownModify(): void {
    this.expirationModifyModalClose$.next(false);
    this.isTimeModifyExpired$.next(false);

    // Setting initial value to missing
    this.missingMinutesModify.minutes = this.expirationTimeMinutesModify;

    this.missingMinutesModifyExpiration$.next(this.missingMinutesModify);

    this.lastExpirationTimeModify = moment().add(this.expirationTimeMinutesModify, 'minutes');

    this.isCountdownModifyActive = true;

    this.isModalSubscriptionModifyActive = true;

    // Timer which will emmit an event every second in order to track the time elapsed
    const missingExpirationTime$ = timer(0, 1000).pipe(takeWhile(() => this.isCountdownModifyActive));

    // Subscription to track the time elapsed
    missingExpirationTime$.subscribe(() => {
      const currentTime = moment();

      // Calculates whether the time is expired
      const isExpiredTime = currentTime.isAfter(this.lastExpirationTimeModify);

      // Calculates missing time
      const tmpMissingSeconds = this.lastExpirationTimeModify.diff(currentTime, 'seconds');

      this.missingMinutesModify.minutes = Math.ceil(tmpMissingSeconds / 60);
      this.missingMinutesModify.seconds = tmpMissingSeconds;

      // Emits new missing time
      this.missingMinutesModifyExpiration$.next(this.missingMinutesModify);

      // Verifies whether the time is expired
      if (isExpiredTime && !this.delayET) {
        [this.missingMinutesModify.minutes, this.missingMinutesModify.seconds] = [0, 0];

        // Emits the last minute missing
        this.missingMinutesModifyExpiration$.next(this.missingMinutesModify);

        // Confirms that the time is expired
        this.isTimeModifyExpired$.next(true);

        // Stops countdown
        this.stopCountdownModify();
        this.stopCountdown();
      }
    });

    this.expirationModifyModalClose$
      .pipe(takeWhile(() => this.isModalSubscriptionModifyActive))
      .subscribe((isModalClose) => {
        if (isModalClose) {
          this.pathsVisited = [];
          this.router.navigate(['/app/orders-products-catalog/order-detail'], {
            queryParams: { ...this.orderParamsModify },
          } as NavigationExtras);
          this.isModalSubscriptionModifyActive = false;
        }
      });
  }

  /**
   * @description Inits the countdown
   */
  startCountdown(): void {
    if (!this.isFromSummary && !this.isCountdownActive) {
      // Resetting variables
      this.expirationModalClose$.next(false);

      this.isTimeExpired$.next(false);

      // Setting initial value to missing
      this.missingMinutes.minutes = this.expirationTimeMinutes;

      // Emits the first missing minute
      this.missingMinutesExpiration$.next(this.missingMinutes);

      // Sets the time when the time will expire
      this.lastExpirationTime = moment().add(this.expirationTimeMinutes, 'minutes');

      // Actives the countdown
      this.isCountdownActive = true;

      // Active modal subscription
      this.isModalSubscriptionActive = true;

      // Timer which will emmit an event every second in order to track the time elapsed
      const missingExpirationTime$ = timer(0, 1000).pipe(takeWhile(() => this.isCountdownActive));

      // Subscription to track the time elapsed
      missingExpirationTime$.subscribe(() => {
        const currentTime = moment();

        // Calculates missing time
        const tmpMissingSeconds = this.lastExpirationTime.diff(currentTime, 'seconds');

        this.missingMinutes.minutes = Math.ceil(tmpMissingSeconds / 60);
        this.missingMinutes.seconds = tmpMissingSeconds;

        // Emits new missing time
        if (this.missingMinutes.minutes >= 0) {
          this.missingMinutesExpiration$.next(this.missingMinutes);
        }

        // Determines whether time has expired
        const isExpiredTime = this.missingMinutes.minutes <= 0;

        // Verifies whether the time is expired
        if (isExpiredTime && !this.delayET && !this.isMultiProdcuct) {
          [this.missingMinutes.minutes, this.missingMinutes.seconds] = [0, 0];

          // Emits the last minute missing
          this.missingMinutesExpiration$.next(this.missingMinutes);

          // Confirms that the time is expired
          this.isTimeExpired$.next(true);

          // Stops countdown
          this.stopCountdown();
        }
      });

      this.expirationModalClose$.pipe(takeWhile(() => this.isModalSubscriptionActive)).subscribe((isModalClose) => {
        if (isModalClose) {
          if (this.isInSummary()) {
            this.isFromSummary = true;
            this.router.navigateByUrl(this.orderFormPath);
          }

          this.isModalSubscriptionActive = false;
        }
      });
    }
  }

  /**
   *  @description Identifies if user is in summary
   * @returns {boolean}
   */
  public isInSummary(): boolean {
    if (!this.currentPath) {
      return false;
    }
    const isInExpirationTimePath = this.expirationTimepaths.filter((path) => this.currentPath.includes(path));
    return isInExpirationTimePath.length && !this.orderFormPath.includes(isInExpirationTimePath[0]);
  }

  /**
   * @description Stops countdown
   */
  stopCountdown(): void {
    this.isCountdownActive = false;
    this.isFromSummary = false;
    this.formValidated = null;
  }

  stopCountdownModify() {
    this.isCountdownModifyActive = false;
  }

  /**
   * @description Set time expiration time by country
   * @param {string} country
   */
  setExpirationTime(): void {
    this.store
      .select(getParameters)
      .pipe(
        take(1),
        tap((parameters) => {
          const parameter = parameters.find(
            (parameterObj) => parameterObj.parameterDesc === ORDERS_TIMEOUT_PARAMETER_CODE,
          );
          if (parameter) {
            this.expirationTimeMinutes = Number(parameter.parameterValue);
          }
        }),
      )
      .subscribe();
  }

  /**
   * @description Set time expiration time of modify by country
   * @param {string} country
   */
  setExpirationTimeModify(): void {
    this.store
      .select(getParameters)
      .pipe(
        take(1),
        tap((parameters) => {
          const parameter = parameters.find(
            (parameterObj) => parameterObj.parameterDesc === MODIFY_TIMEOUT_PARAMETER_CODE,
          );
          if (parameter) {
            this.expirationTimeMinutesModify = Number(parameter.parameterValue);
          }
        }),
      )
      .subscribe();
  }

  /**
   * @description Check if a translation key exists.
   * @param {string} key
   * @param {string} backup
   * @returns {string}
   */
  public translateExist(key: string, backup: string): string {
    // Get the translation
    const transKey = this.translate.instant(key);

    // Check if the key exists in the translations
    if (transKey !== key) {
      // Return the translation
      return transKey;
    }

    // Return the backup
    return backup;
  }

  ngOnDestroy() {
    this.stopCountdown();
    this.ruterSubscription$.unsubscribe();
  }
}
